import dayjs from "dayjs";
import React from "react";
import { DatePicker, Input } from "@pd/pd-ui-kit";
import { Control, Controller } from "react-hook-form";

import {
  type FilterField as FilterFieldI,
  type FilterFieldOption,
  type FilterFieldOptionsHookResult,
} from "~/features/filter/model/types";
import { filterOptionByLabel } from "~/shared/lib/filterHelpers";
import { Select } from "~/shared/ui/Select";

import cls from "./FilterRoot.module.less";

interface FilterFieldProps {
  field: FilterFieldI;
  control: Control<any>;
  /** setValue из useForm — нужен для сброса зависимых полей (resetFieldsOnChange). */
  setValue?: (name: string, value: unknown) => void;
}

/** Записывает значение поля и сбрасывает зависимые поля каскада. */
const applyFieldChange = (
  field: FilterFieldI,
  setValue: FilterFieldProps["setValue"],
  onChange: (value: unknown) => void,
  next: unknown,
) => {
  onChange(next);
  if (field.resetFieldsOnChange && setValue) {
    field.resetFieldsOnChange.forEach((name) => setValue(name, null));
  }
};

export const FilterField = (props: FilterFieldProps) => {
  const { field, control, setValue } = props;

  if (field.type === "input") {
    return <FilterInputField field={field} control={control} />;
  }

  if (field.type === "select") {
    return (
      <FilterSelectField field={field} control={control} setValue={setValue} />
    );
  }

  if (field.type === "datePicker") {
    return <FilterDatePickerField field={field} control={control} />;
  }

  if (field.type === "dateRange") {
    return <FilterDateRangeField field={field} control={control} />;
  }

  return null;
};

const FilterInputField = ({ field, control }: FilterFieldProps) => (
  <Controller
    control={control}
    name={field.name}
    render={({ value, onChange, onBlur }) => (
      <Input
        value={(value as string) ?? ""}
        placeholder={field.placeholder}
        disabled={field.disabled}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        allowClear
      />
    )}
  />
);

const FilterSelectField = ({ field, control, setValue }: FilterFieldProps) => {
  const hookResult: FilterFieldOptionsHookResult | undefined =
    field.optionsHook?.(control);
  const options: FilterFieldOption[] =
    hookResult?.options ?? field.options ?? [];

  const handlePopupScroll: React.UIEventHandler<HTMLDivElement> = (e) => {
    if (!hookResult?.onScrollBottom) return;
    const target = e.currentTarget;
    if (target.scrollTop + target.clientHeight >= target.scrollHeight - 16) {
      hookResult.onScrollBottom();
    }
  };

  return (
    <Controller
      control={control}
      name={field.name}
      render={({ value, onChange }) => {
        // Мультивыбор: значение формы — массив строк, пустой массив → undefined,
        // чтобы поле не попадало в URL и срабатывал placeholder.
        if (field.multiple) {
          const multipleValue = Array.isArray(value)
            ? (value as unknown[]).map(String)
            : value == null || value === ""
              ? undefined
              : [String(value)];
          return (
            <Select
              allowClear
              showSearch
              mode="multiple"
              disabled={field.disabled}
              // null — показать все теги: pd-ui-kit по умолчанию ставит
              // "responsive" и схлопывает лишние в «+ N», а null/undefined
              // у него включают этот дефолт, поэтому подменяем на число,
              // заведомо большее количества значений
              maxTagCount={
                field.maxTagCount === null
                  ? Number.MAX_SAFE_INTEGER
                  : field.maxTagCount
              }
              placeholder={field.placeholder}
              options={options}
              loading={hookResult?.loading}
              value={
                multipleValue && multipleValue.length
                  ? multipleValue
                  : undefined
              }
              onChange={(next) =>
                applyFieldChange(
                  field,
                  setValue,
                  onChange,
                  Array.isArray(next) && next.length
                    ? next.map(String)
                    : undefined,
                )
              }
              onPopupScroll={handlePopupScroll}
              onSearch={hookResult?.searchOptions}
              filterOption={
                hookResult?.searchOptions ? false : filterOptionByLabel
              }
            />
          );
        }

        return (
          <Select
            allowClear
            showSearch
            disabled={field.disabled}
            placeholder={field.placeholder}
            options={options}
            loading={hookResult?.loading}
            value={
              value == null || value === ""
                ? undefined
                : (String(value) as string)
            }
            onChange={(next) =>
              applyFieldChange(
                field,
                setValue,
                onChange,
                next == null ? undefined : String(next),
              )
            }
            onPopupScroll={handlePopupScroll}
            onSearch={hookResult?.searchOptions}
            filterOption={
              hookResult?.searchOptions ? false : filterOptionByLabel
            }
          />
        );
      }}
    />
  );
};

const datePickerProps = (
  field: FilterFieldI,
  value: unknown,
  onChange: (v: string | null) => void,
  placeholder?: string,
) => {
  const isMonth = field.picker === "month";
  const valueFormat = isMonth ? "YYYY-MM" : "YYYY-MM-DD";
  return {
    allowClear: true,
    disabled: field.disabled,
    style: { width: "100%" },
    placeholder: placeholder ?? field.placeholder,
    picker: field.picker ?? "date",
    format: isMonth ? "MM.YYYY" : "DD.MM.YYYY",
    value: value ? dayjs(String(value), valueFormat) : null,
    onChange: (date: Parameters<typeof dayjs>[0] | null) =>
      onChange(date ? dayjs(date).format(valueFormat) : null),
    ...(field.disableFutureDates
      ? {
          disabledDate: (current: ReturnType<typeof dayjs> | null) =>
            current ? current.isAfter(dayjs(), "day") : false,
        }
      : {}),
  };
};

/**
 * Дата хранится в форме строкой `yyyy-MM-dd` — так её можно
 * сериализовать в URL дефолтным сериализатором без кастомного serialize/parse.
 */
const FilterDatePickerField = ({ field, control }: FilterFieldProps) => (
  <Controller
    control={control}
    name={field.name}
    render={({ value, onChange }) => (
      <DatePicker {...datePickerProps(field, value, onChange)} />
    )}
  />
);

/**
 * Пара связанных дат («с»/«по») под одним заголовком, через тире.
 * Значения хранятся в двух независимых полях формы: `name` и `nameTo`.
 */
const FilterDateRangeField = ({ field, control }: FilterFieldProps) => (
  <div className={cls.FilterDateRange}>
    <Controller
      control={control}
      name={field.name}
      render={({ value, onChange }) => (
        <DatePicker {...datePickerProps(field, value, onChange)} />
      )}
    />
    <span className={cls.FilterDateRangeSeparator}>—</span>
    <Controller
      control={control}
      name={field.nameTo as string}
      render={({ value, onChange }) => (
        <DatePicker
          {...datePickerProps(field, value, onChange, field.placeholderTo)}
        />
      )}
    />
  </div>
);
